import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { clearLocalPlaceholder, getLocalPlaceholder } from './pending-uploads';
import { assetAvailability } from '../../services/asset-availability';

/**
 * Loader et bilde først når asset-availability-servicen har bekreftet at
 * `assetPath` finnes i library. Tidligere pollet vi selve URL-en og retry-et
 * på 404 (60 forsøk × 2s plateau) — det skapte 404-spam i serverlogger og
 * kunne se ut som ekte feil. Nå abonnerer vi på service-en, og setter `<img>`
 * sin src først når library-bekreftelse er på plass.
 *
 * Hvis `assetPath` er tom, faller vi tilbake til umiddelbar lasting (samme
 * oppførsel som før, brukbart utenfor signage-konteksten).
 *
 * Optional placeholder: ferskt opplastede bilder ligger i pending-uploads-
 * registeret som dataUrl. Hvis vi finner et match for `src` viser vi det
 * umiddelbart mens vi venter på bekreftelse — så brukeren slipper sort felt.
 * Konsumenter kan også sende inn en `placeholder` (typisk library-thumbnailen,
 * en liten base64 data-URL) → blur-up: vi viser den oppskalert/blurret med en
 * gang og bytter til det fulle bildet når det er ferdig dekodet.
 *
 * Decode-før-bytt: vi venter på `img.decode()` før det fulle bildet gjøres
 * synlig. Da slipper vi at et stort bilde males halvferdig (top-down) over en
 * treg strøm, og at hovedtråden hakker når bildet først skal paintes.
 *
 * No loading spinner: backend returnerer content-addressed assets med
 * `Cache-Control: immutable`, så cachede bilder lastes instant og første-
 * lastinger er raske nok til at en spinner blir visual støy.
 *
 * Layout-agnostic: host has no intrinsic sizing. Style the host (or the
 * inner `img` via `::part(img)`) from the consumer to control dimensions.
 */
@customElement('d-loading-image')
export class DLoadingImage extends LitElement {
  static readonly styles = css`
    :host {
      display: inline-block;
      position: relative;
      line-height: 0;
    }
    img {
      display: block;
    }
    img.placeholder {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: inherit;
    }
    /* Blur-up: en liten thumbnail oppskaleres, så vi blurrer den og skalerer
       litt opp for å skjule de uskarpe kantene mens fullbildet lastes. */
    img.placeholder.blur {
      filter: blur(12px);
      transform: scale(1.06);
    }
    img.real:not(.loaded) {
      visibility: hidden;
    }
    .error {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 24px;
      min-height: 24px;
      background: #f3f4f6;
    }
    .error svg {
      width: 40%;
      max-width: 48px;
      min-width: 20px;
      height: auto;
      color: #9ca3af;
    }
  `;

  @property({ type: String })
  src = '';

  /**
   * Kanonisk path (f.eks. `org/abc.jpg`) — brukes til oppslag mot
   * asset-availability-servicen. Når blank: lastes umiddelbart uten å vente
   * på library-bekreftelse.
   */
  @property({ type: String, attribute: 'asset-path' })
  assetPath = '';

  @property({ type: String })
  alt = '';

  /**
   * Valgfri blur-up-placeholder (typisk library-thumbnailen som base64
   * data-URL). Vises oppskalert/blurret til det fulle bildet er dekodet.
   * Lokalt opplastede placeholders (pending-uploads) har forrang siden de er
   * det faktiske bildet i full kvalitet.
   */
  @property({ type: String })
  placeholder = '';

  /** Native lazy-loading-hint for det indre `<img>`. Galleriet bruker 'lazy'. */
  @property({ type: String })
  loading: 'eager' | 'lazy' = 'eager';

  @state() private loaded = false;
  @state() private ready = false;
  @state() private localPlaceholder = '';
  @state() private errored = false;
  // Cache-busting suffix used to re-attempt a load after a transient error.
  @state() private retryNonce = 0;

  @query('img.real') private imgEl?: HTMLImageElement;

  private unsubscribe?: () => void;
  private retryAttempt = 0;
  private retryTimer?: number;

  // A library-confirmed asset can briefly 404 right after upload (read-after-write
  // / propagation race, or a transient proxy/S3 hiccup). Retry a few times before
  // showing the permanent "missing" fallback instead of giving up on a single error.
  private static readonly MAX_LOAD_RETRIES = 8;
  private static readonly RETRY_DELAY_MS = 1500;

  connectedCallback(): void {
    super.connectedCallback();
    this.subscribeToAvailability();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.clearRetry();
  }

  protected updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('assetPath')) {
      this.subscribeToAvailability();
    }
    if (changedProps.has('src')) {
      this.loaded = false;
      this.errored = false;
      this.clearRetry();
      this.retryNonce = 0;
      this.localPlaceholder = this.src ? (getLocalPlaceholder(this.src) ?? '') : '';
    }
  }

  private clearRetry(): void {
    this.retryAttempt = 0;
    if (this.retryTimer !== undefined) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
  }

  /** The `<img>` src, with a retry nonce appended so a re-attempt bypasses the cache. */
  private get realSrc(): string {
    if (!this.retryNonce) return this.src;
    return `${this.src}${this.src.includes('?') ? '&' : '?'}retry=${this.retryNonce}`;
  }

  private subscribeToAvailability() {
    this.unsubscribe?.();
    this.unsubscribe = assetAvailability.subscribe(this.assetPath, (ready) => {
      this.ready = ready;
    });
  }

  private onLoad = async () => {
    if (!this.ready || !this.src) return;
    const img = this.imgEl;
    // Decode-før-bytt: vent på at bildet er ferdig dekodet før vi gjør det
    // synlig, så vi unngår halv-malt bilde / paint-hakk. decode() kan rejecte
    // hvis src endres eller elementet fjernes underveis — da faller vi tilbake
    // til å vise bildet uansett (load-eventet betyr at nedlastingen lyktes).
    if (img && typeof img.decode === 'function') {
      const decodingSrc = this.realSrc;
      try {
        await img.decode();
      } catch {
        /* fall through */
      }
      // Guard: src/realSrc kan ha endret seg mens vi ventet på decode.
      if (!this.ready || !this.src || this.realSrc !== decodingSrc) return;
    }
    this.clearRetry();
    this.loaded = true;
    this.localPlaceholder = '';
    if (this.src) clearLocalPlaceholder(this.src);
    if (this.imgEl) {
      this.dispatchEvent(
        new CustomEvent<{ naturalWidth: number; naturalHeight: number }>('image-loaded', {
          bubbles: true,
          composed: true,
          detail: {
            naturalWidth: this.imgEl.naturalWidth,
            naturalHeight: this.imgEl.naturalHeight,
          },
        }),
      );
    }
  };

  // En library-bekreftet asset kan 404'e rett etter opplasting (read-after-write
  // / propagerings-race mot S3/proxy). For slike (`assetPath` satt) retry-er vi
  // noen ganger med cache-bust før vi gir opp — ellers ble en enkelt transient
  // feil permanent «bilde mangler» selv om objektet finnes (#13236-oppfølging).
  //
  // Uten tracking (`assetPath` tom) finnes ingen bekreftelse å vente på, så vi
  // gir opp umiddelbart. Ved endelig feil viser vi en synlig «bilde mangler»-
  // fallback og varsler containeren via `image-error` (f.eks. d-slide-media).
  private onError = () => {
    if (!this.src) return;

    if (this.assetPath && this.retryAttempt < DLoadingImage.MAX_LOAD_RETRIES) {
      this.retryAttempt++;
      this.retryTimer = window.setTimeout(() => {
        this.retryTimer = undefined;
        // Bumper nonce → realSrc endres → `<img>` laster på nytt (cache-bypass).
        this.retryNonce = this.retryAttempt;
      }, DLoadingImage.RETRY_DELAY_MS);
      return;
    }

    console.warn('[d-loading-image] failed to load:', this.src);
    this.errored = true;
    this.dispatchEvent(
      new CustomEvent<{ src: string }>('image-error', {
        bubbles: true,
        composed: true,
        detail: { src: this.src },
      }),
    );
  };

  private renderErrorFallback() {
    return html`
      <div class="error" role="img" aria-label="Bilde mangler">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
          <path d="M3 3l18 18" />
        </svg>
      </div>
    `;
  }

  render() {
    // Lokalt opplastet placeholder (det faktiske bildet) har forrang og vises
    // skarp; ellers brukes den innsendte blur-up-thumbnailen (blurres).
    const placeholderSrc = this.localPlaceholder || this.placeholder;
    const placeholderIsThumb = !this.localPlaceholder && !!this.placeholder;
    return html`
      ${placeholderSrc && !this.loaded
        ? html`<img
            class="placeholder ${placeholderIsThumb ? 'blur' : ''}"
            src="${placeholderSrc}"
            alt=""
            aria-hidden="true"
            draggable="false"
          />`
        : nothing}
      ${this.ready && this.src && !this.errored
        ? html`<img
            part="img"
            class="real ${this.loaded ? 'loaded' : ''}"
            src="${this.realSrc}"
            alt="${this.alt}"
            draggable="false"
            decoding="async"
            loading="${this.loading}"
            @load=${this.onLoad}
            @error=${this.onError}
          />`
        : nothing}
      ${this.errored ? this.renderErrorFallback() : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-loading-image': DLoadingImage;
  }
}
